import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import {
  Briefcase,
  Euro,
  FileCheck,
  Building2,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Receipt,
  ShieldCheck
} from 'lucide-react';

const BusinessSetupPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="business-setup-page">
      <Navbar />

      {/* Hero */}
      <section className="py-16 md:py-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-100/40 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Briefcase className="w-4 h-4" />
            Integration Module
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-4">
            Opening a Business in Germany
          </h1>
          <p className="text-xl text-babyblue-600 leading-relaxed max-w-3xl mx-auto">
            Different paths, different tax treatment. Pick the structure that fits your scale.
          </p>
        </div>
      </section>

      {/* Kleinunternehmer */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 md:p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-emerald-100 rounded-xl flex-shrink-0">
                <Sparkles className="w-7 h-7 text-emerald-700" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-babyblue-800">Kleinunternehmer</h2>
                <p className="text-babyblue-500 italic text-sm">Small entrepreneur — the easiest entry path</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-5">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Euro className="w-5 h-5 text-emerald-700" />
                  <span className="font-bold text-emerald-900">Revenue Cap</span>
                </div>
                <p className="text-2xl font-bold text-emerald-900">€25,000</p>
                <p className="text-sm text-emerald-800">gross income per year (2025 limit)</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Receipt className="w-5 h-5 text-amber-700" />
                  <span className="font-bold text-amber-900">Tax-Free Allowance</span>
                </div>
                <p className="text-2xl font-bold text-amber-900">€12,348</p>
                <p className="text-sm text-amber-800">Grundfreibetrag — income below this is tax-free (2026)</p>
              </div>
            </div>

            <h3 className="font-bold text-babyblue-800 text-lg mb-3 mt-6">How to register (Gewerbeanmeldung)</h3>
            <ol className="space-y-3">
              {[
                'Register your business at the local Rathaus (town hall).',
                'Google your city + "Gewerbeanmeldung" — most cities now have it online.',
                'Fill out personal info + purpose of business — registration is done in minutes.',
                'A few days later you receive a written, signed confirmation by post.',
                'Pay the registration fee — typically €20–40 (varies by Kommune / municipality).',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`klein-step-${i}`}>
                  <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {i + 1}
                  </div>
                  <p className="text-babyblue-700">{step}</p>
                </li>
              ))}
            </ol>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mt-5">
              <p className="text-blue-900 text-sm">
                <strong>Heads-up:</strong> Procedures vary by Kommune. Always check your local Rathaus website for exact steps and fees.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* GmbH */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 md:p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-blue-100 rounded-xl flex-shrink-0">
                <Building2 className="w-7 h-7 text-blue-700" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-babyblue-800">GmbH</h2>
                <p className="text-babyblue-500 italic text-sm">Gesellschaft mit beschränkter Haftung — similar to the UK "Ltd."</p>
              </div>
            </div>

            <p className="text-babyblue-700 leading-relaxed mb-5">
              The biggest difference from the Kleinunternehmer model: you can earn unlimited revenue, and you are <strong>not personally liable</strong> with your private estate. Your liability is capped at the company's share capital.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-5">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Euro className="w-5 h-5 text-blue-700" />
                  <span className="font-bold text-blue-900">Share Capital</span>
                </div>
                <p className="text-2xl font-bold text-blue-900">€25,000</p>
                <p className="text-sm text-blue-800">required as <em>Stammkapital</em> (at least €12,500 paid in at founding)</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="w-5 h-5 text-emerald-700" />
                  <span className="font-bold text-emerald-900">Liability</span>
                </div>
                <p className="text-lg font-bold text-emerald-900">Limited</p>
                <p className="text-sm text-emerald-800">Your private money &amp; property are protected.</p>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <p className="text-amber-900 text-sm">
                <strong>Note:</strong> A GmbH also requires a notarised founding contract, registration in the <em>Handelsregister</em>, and ongoing bookkeeping (often with a Steuerberater).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Other forms */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-2xl font-bold text-babyblue-800 mb-6 text-center">
            Other Legal Forms (Briefly)
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-babyblue-50 rounded-xl border border-babyblue-200 p-5" data-testid="form-ag">
              <h3 className="font-bold text-babyblue-800 mb-1">AG</h3>
              <p className="text-xs text-babyblue-500 italic mb-2">Aktiengesellschaft</p>
              <p className="text-sm text-babyblue-700">
                Stock corporation. Minimum €50,000 capital. Used by larger companies that want to issue shares.
              </p>
            </div>
            <div className="bg-babyblue-50 rounded-xl border border-babyblue-200 p-5" data-testid="form-ohg">
              <h3 className="font-bold text-babyblue-800 mb-1">OHG</h3>
              <p className="text-xs text-babyblue-500 italic mb-2">Offene Handelsgesellschaft</p>
              <p className="text-sm text-babyblue-700">
                General partnership — two or more people run a business together. All partners are fully liable.
              </p>
            </div>
            <div className="bg-babyblue-50 rounded-xl border border-babyblue-200 p-5" data-testid="form-kg">
              <h3 className="font-bold text-babyblue-800 mb-1">KG</h3>
              <p className="text-xs text-babyblue-500 italic mb-2">Kommanditgesellschaft</p>
              <p className="text-sm text-babyblue-700">
                Limited partnership — one general partner (full liability) plus limited partners (liability up to their stake).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-xl font-bold text-babyblue-800 mb-6">Continue Your Integration</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { icon: '📄', title: 'Visa & Immigration', path: '/integration/visa' },
              { icon: '🏠', title: 'Finding Housing', path: '/integration/housing' },
              { icon: '🎓', title: 'Education', path: '/integration/education' },
            ].map((item, i) => (
              <Link key={i} to={item.path} className="bg-white rounded-xl border border-babyblue-200 p-4 flex items-center gap-3 hover:border-primary hover:shadow-md transition-all">
                <span className="text-2xl">{item.icon}</span>
                <span className="text-babyblue-700 font-medium">{item.title}</span>
                <ArrowRight className="w-4 h-4 text-babyblue-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BusinessSetupPage;
